import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Receipt, Download, FileText, Loader2 } from 'lucide-react';
import NavBar from '../components/NavBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvoicesPage = () => {
  const { language } = useLanguage();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const res = await axios.get(`${API}/invoices`);
      setInvoices(res.data);
    } catch (err) {
      console.error('Error fetching invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoicePdf = (invoiceId) => {
    window.open(`${API}/invoices/${invoiceId}/pdf`, '_blank');
  };

  const tierNames = {
    executive_scan: 'Executive Scan',
    operational: 'Operational',
    enterprise: 'Enterprise',
    strategic: 'Strategic Intelligence'
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="invoices-page">
      <NavBar />
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Receipt className="h-6 w-6 text-blue-400" />
            {language === 'tr' ? 'Faturalarım' : 'My Invoices'}
          </h1>
          <p className="text-slate-400 mt-1">
            {language === 'tr' ? 'Tüm ödeme faturalarınızı görüntüleyin ve indirin.' : 'View and download all your payment invoices.'}
          </p>
        </div>

        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
              </div>
            ) : invoices.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-slate-400">{language === 'tr' ? 'Fatura No' : 'Invoice No'}</TableHead>
                    <TableHead className="text-slate-400">{language === 'tr' ? 'Tarih' : 'Date'}</TableHead>
                    <TableHead className="text-slate-400">{language === 'tr' ? 'Paket' : 'Plan'}</TableHead>
                    <TableHead className="text-slate-400">{language === 'tr' ? 'Tutar' : 'Amount'}</TableHead>
                    <TableHead className="text-slate-400">{language === 'tr' ? 'Durum' : 'Status'}</TableHead>
                    <TableHead className="text-slate-400">{language === 'tr' ? 'İndir' : 'Download'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((inv) => (
                    <TableRow key={inv.id} className="border-slate-700">
                      <TableCell className="text-white font-mono text-sm">{inv.invoice_number}</TableCell>
                      <TableCell className="text-slate-300">{formatDate(inv.created_at)}</TableCell>
                      <TableCell className="text-slate-300">{tierNames[inv.tier] || inv.tier}</TableCell>
                      <TableCell className="text-white font-medium">
                        {inv.amount} {inv.currency}
                        {inv.discount_percent > 0 && (
                          <span className="ml-2 text-xs text-emerald-400">-%{inv.discount_percent}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          inv.status === 'paid' 
                            ? 'bg-emerald-500/20 text-emerald-400' 
                            : 'bg-amber-500/20 text-amber-400'
                        }`}>
                          {inv.status === 'paid' 
                            ? (language === 'tr' ? 'Ödendi' : 'Paid') 
                            : (language === 'tr' ? 'Beklemede' : 'Pending')}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadInvoicePdf(inv.id)}
                          className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                          data-testid={`download-invoice-${inv.id}`}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          PDF
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 mb-2">
                  {language === 'tr' ? 'Henüz faturanız bulunmuyor.' : 'No invoices yet.'}
                </p>
                <p className="text-slate-500 text-sm">
                  {language === 'tr' ? 'Bir paket satın aldığınızda faturanız burada görünecektir.' : 'Invoices will appear here after you purchase a plan.'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default InvoicesPage;
